import { AriaAttributes, ReactNode, useMemo } from 'react';
import { BaseRowData } from '@adv-frontend/data-provider';
import { SelectionMode } from '@react-types/shared';

import { ListBoxItem, ListBoxOptionItem } from '../ListBox';

import {
    MultiSelectProps,
    MultiSelectWithoutDataProviderProps,
    SelectPopupWidth,
    SelectProps,
    SelectWidth,
} from './types';
import { MultiSelectState } from './hooks/useMultiSelectState';

/**
 * Ширина ПОПАПА. Приоритет:
 * 1. явный `popupWidth`;
 * 2. `'max'` (попап = ширине триггера) — только при `width='max'` или deprecated
 *    `matchTriggerWidth` (обратная совместимость);
 * 3. иначе `'auto'` (по контенту).
 *
 * Конкретная ширина триггера (`'200px'`/`'50%'`) на попап **не** влияет —
 * его задают отдельно через `popupWidth`.
 */
export const resolvePopupWidth = (
    popupWidth: SelectPopupWidth | undefined,
    width: SelectWidth | undefined,
    matchTriggerWidth: boolean | undefined,
): SelectPopupWidth => {
    if (popupWidth !== undefined) {
        return popupWidth;
    }

    return width === 'max' || matchTriggerWidth ? 'max' : 'auto';
};

/**
 * Возвращает строку или ReactNode для отображения в триггере селекта
 * @param item — элемент
 * @param allowReactNode можно ли возвращать не строку (разрешено при отсутствии мультивыбора)
 * @returns строка или ReactNode
 */
export function getItemTitle<V extends string>(item: ListBoxOptionItem<V>): string;
export function getItemTitle<V extends string>(item: ListBoxOptionItem<V>, allowReactNode: true): ReactNode;
export function getItemTitle<V extends string>(item: ListBoxOptionItem<V>, allowReactNode?: true): ReactNode | string {
    const { label, content, value } = item;

    if (label) {
        return label;
    }

    if (allowReactNode || typeof content === 'string') {
        return content ?? value;
    }

    return value;
}

export function getSelectTriggerContent<ValueType extends string>(value: ListBoxOptionItem<ValueType>[]): ReactNode {
    if (value.length === 1 && value[0]) {
        // Если нет мультивыбора, то можно возвращать любую ReactNode, а не только строчку
        return getItemTitle<ValueType>(value[0], true);
    }

    const items = value.map(item => getItemTitle<ValueType>(item)).filter(Boolean);

    return items.length > 0 ? items.join(', ') : null;
}

/**
 * Достает из ListBox value значение help,
 * работает только в случае если выбран один элемент
 * @param value массив выбранных значений
 * @returns функцию help, лежащую в выбранном значении, если значений более одного или ноль, то вернет undefined
 */
export function getSelectTriggerHelp<ValueType extends string>(
    value: ListBoxOptionItem<ValueType>[],
): ListBoxOptionItem<ValueType>['help'] | undefined {
    const firstSelectedItem = value[0];
    if (value.length !== 1 || !firstSelectedItem) {
        return undefined;
    }

    return firstSelectedItem.help;
}

export function isMultiSelectPropsWithoutDataProvider<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
>(
    props: Partial<SelectProps<ValueType, SectionType, RowData>>,
): props is Partial<MultiSelectWithoutDataProviderProps<ValueType, SectionType>> {
    return isMultiSelectProps(props) && !('dataProvider' in props);
}

export function isMultiSelectProps<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
>(
    props: Partial<SelectProps<ValueType, SectionType, RowData>>,
): props is Partial<MultiSelectProps<ValueType, SectionType, RowData>> {
    return Array.isArray(props.value);
}

export function getSelectValues<ValueType extends string, SectionType extends string = ValueType>(
    items: ListBoxItem<ValueType, SectionType>[],
): ValueType[] {
    return items.reduce<ValueType[]>((acc, item) => {
        if ('options' in item) {
            acc.push(...getSelectValues(item.options));

            return acc;
        }

        acc.push(item.value);
        return acc;
    }, []);
}

export function useAriaProps(props: AriaAttributes & { validationState?: 'valid' | 'invalid' }) {
    return {
        'aria-required': props['aria-required'],
        'aria-invalid': props['aria-invalid'] || props.validationState === 'invalid',
        'aria-labelledby': props['aria-labelledby'],
        'aria-errormessage': props['aria-errormessage'],
        'aria-describedby': props['aria-describedby'],
    };
}

export function useCommonSelectProps<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
>(props: SelectProps<ValueType, SectionType, RowData>) {
    const { value, isOpen, autoFocus, onOpen, onClose, label } = props;
    const defaultSingleKey = !Array.isArray(value) && value ? [value] : [];
    const selectionMode: SelectionMode = Array.isArray(value) ? 'multiple' : 'single';
    const type = 'listbox';

    const ariaProps = useAriaProps(props);

    return {
        selectedKeys: Array.isArray(value) ? value : defaultSingleKey,
        isOpen: isOpen,
        autoFocus,
        type,
        selectionMode: selectionMode,
        disallowEmptySelection: selectionMode === 'single',
        onOpenChange: (isOpen: boolean) => {
            if (isOpen) {
                onOpen?.();
            } else {
                onClose?.();
            }
        },
        ...ariaProps,
        'aria-label': props['aria-label'],
        label,
    };
}

export function usePinnedSections<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
    StateType extends object = object,
>(props: SelectProps<ValueType, SectionType, RowData>, state: MultiSelectState<StateType>) {
    const {
        addonBeforeSection: propsAddonBeforeSection,
        pinnedSectionAfter: propsPinnedSectionAfter,
        pinnedSection: propsPinnedSection,
    } = props;

    const addonBeforeSection = useMemo(
        () =>
            typeof propsAddonBeforeSection === 'function'
                ? propsAddonBeforeSection(state.close)
                : propsAddonBeforeSection,
        [propsAddonBeforeSection, state.close],
    );

    const pinnedSection = useMemo(
        () => (typeof propsPinnedSection === 'function' ? propsPinnedSection(state.close) : propsPinnedSection),
        [propsPinnedSection, state.close],
    );

    const pinnedSectionAfter = useMemo(
        () =>
            typeof propsPinnedSectionAfter === 'function'
                ? propsPinnedSectionAfter(state.close)
                : propsPinnedSectionAfter,
        [propsPinnedSectionAfter, state.close],
    );

    return { pinnedSection, addonBeforeSection, pinnedSectionAfter };
}
